import {computed, type ComputedRef, ref, type Ref, watch} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useMoods} from '@/ts/composables/pure/use-moods';
import {useFeelingSuggestions} from '@/ts/composables/pure/use-feeling-suggestions';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import type {JournalEntryDraft, JournalFeelingOption, StackEntry} from '@/ts/types/component/today-page';
import type {JournalEntryDTO} from '@/ts/types/dto/journals-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

/** What the journal tile needs, and the one call that feeds it back. */
interface JournalTileData {
  /** Derived from the payload the page was handed, so it changes when the page reloads it. */
  entries: ComputedRef<StackEntry[]>;
  feelingsByMood: Ref<Record<string, JournalFeelingOption[]>>;
  defaultFeelings: Ref<JournalFeelingOption[]>;
  loading: Ref<boolean>;
  saving: Ref<boolean>;
  load(): Promise<void>;
  /** Resolves true once the entry is stored, so the caller can confirm it to the tile. */
  save(draft: JournalEntryDraft): Promise<boolean>;
}

/**
 * How many of the payload's entries the stack considers. It draws three, but an
 * entry written on the old five-point scale that maps to no mood is dropped at
 * read time, so taking exactly three could leave it short. Ten is the smallest
 * number that still fills the stack when most of a user's recent history is
 * legacy. The payload carries twenty, so the rest is simply not looked at.
 */
const STACK_SIZE = 10;

/**
 * The id of an entry the user has just written, which the backend has not named yet.
 *
 * It is the `v-for` key of the pile, so it has to be unique rather than merely different from the
 * ids the payload carries. A timestamp is neither: two entries minted in the same millisecond
 * collide and Vue then reuses the wrong card. Minted here rather than in each of the two places
 * that build one of these, so the two cannot drift apart.
 */
export function localEntryId(): string {
  return `local-${crypto.randomUUID()}`;
}

/**
 * Feeds the Today page's journal tile from the journal API.
 *
 * Lives here rather than in the page so the tile has exactly one wiring: the
 * mapping from a journal entry to a card — including the fallback for entries
 * written before the ten moods existed — is the sort of thing that silently
 * drifts when it is copied.
 *
 * Failures go through `useLoadingNotification`, like the rest of the app: an
 * empty tile is indistinguishable from a genuinely empty journal, so a dropped
 * request has to say so rather than render as a believable empty state.
 *
 * WHY THIS IS NOT A SCAFFOLD CONSTANT (the pattern the other Today tiles use):
 * the tile shipped before the page did and has been exercised against the real
 * API since — writing an entry included, which a constant cannot do. The tile is
 * presentational either way; only the source of its view-model differs.
 *
 * The stack comes from `today.journal.entries`, handed in by the page: the same
 * `JournalEntryDTO` this already mapped when it fetched them itself, so the
 * mapping is untouched and only the source moved. The template call stays — the
 * payload carries entries, not the content id or the feelings the form needs —
 * so this composable is still the one place that knows how to save an entry.
 *
 * A saved entry still has to reach the pile, and that is what the refetch this
 * used to do was for: `confirmSaved` puts the new card on the tile's BACK face,
 * which only comes into view on a narrow tile, so on a wide one the pile would
 * otherwise keep showing the three entries from before the user wrote theirs.
 * Refetching the payload for that would blank the whole bento behind a spinner,
 * and refetching `/me/journals` would bring back a second source for the same
 * data — one that answers a slightly different question, since it filters
 * intervention-linked entries out only for users without medical device consent,
 * where the payload always leaves them out. So the entry is put at the head of
 * the pile here instead, exactly as the tile does for its own back face, and the
 * canonical list arrives with the next payload.
 */
export function useJournalTile(payloadEntries: Ref<readonly JournalEntryDTO[]>): JournalTileData {
  const api = useApi();
  const {getMood, getMoodForLegacyLevel} = useMoods();

  const contentId = ref<UuidDTO | null>(null);
  const feelingsByMood = ref<Record<string, JournalFeelingOption[]>>({});
  const defaultFeelings = ref<JournalFeelingOption[]>([]);
  const saved = ref(false);

  /**
   * Cards are drawn from the new mood. Entries written before it exists fall
   * back to the read-time mapping of their legacy level, so real history still
   * shows; one that maps to neither is dropped rather than drawn blank.
   */
  function toStackEntry(entry: JournalEntryDTO, index: number): StackEntry | null {
    const mood = getMood(entry.mood)?.key ?? getMoodForLegacyLevel(entry.moodLevel);
    if (!mood) {
      return null;
    }
    return {
      id: entry.userComponentId ?? `${entry.contentId}-${index}`,
      mood,
      text: entry.answer,
      date: entry.createdAt,
    };
  }

  /**
   * Entries written in this visit, newest first. They are drawn from the draft rather than from a
   * response: the backend echoes the stored entry, but reading it back costs a request the page
   * does not otherwise need, and every field the pile draws is already in hand.
   */
  const justWritten = ref<StackEntry[]>([]);

  // Dropped the moment a new payload lands: it carries the entry that was just written, so keeping
  // the local copy would show the same one twice — different ids, so nothing collapses them. The
  // page reloads the payload after a questionnaire, which is exactly when this bites.
  watch(payloadEntries, () => {
    justWritten.value = [];
  });

  const entries = computed<StackEntry[]>(() => [
    ...justWritten.value,
    ...payloadEntries.value
      .slice(0, STACK_SIZE)
      .map((entry, index) => toStackEntry(entry, index))
      .filter((entry): entry is StackEntry => entry !== null),
  ].slice(0, STACK_SIZE));

  const [load, loading] = useLoadingNotification(async() => {
    const template = await api.me.getJournalTemplate();
    contentId.value = template.journal.contentId;
    // Shared with the Journal tab's entry modal, so the two offer the same suggestions for a mood.
    const suggestions = useFeelingSuggestions(template.availableFeelings);
    feelingsByMood.value = suggestions.byMood;
    defaultFeelings.value = suggestions.fallback;
  });

  const [postEntry, saving] = useLoadingNotification(
    async(draft: JournalEntryDraft) => {
      if (!contentId.value) {
        // No content to attach the entry to: the template never loaded. Throwing
        // surfaces it like any other failure instead of dropping the entry.
        throw new Error('journal template unavailable');
      }
      await api.me.postJournal({
        contentId: contentId.value,
        ...(draft.text && {answer: {answer: draft.text}}),
        ...(draft.feelingIds.length > 0 && {secondaryFeelingIds: draft.feelingIds}),
        mood: draft.mood,
      });
    },
    () => {
      saved.value = true;
    },
  );

  async function save(draft: JournalEntryDraft): Promise<boolean> {
    saved.value = false;
    await postEntry(draft);
    if (saved.value) {
      // Only on success: an entry that never reached the backend must not appear in the pile, or
      // the user is told they wrote something they did not.
      justWritten.value = [
        {
          // Local, and it stays local until the next payload replaces the whole pile. The id only
          // has to be unique among the cards on screen, which the timestamp gives it.
          id: localEntryId(),
          mood: draft.mood,
          text: draft.text || null,
          date: new Date().toISOString(),
        },
        ...justWritten.value,
      ];
    }
    return saved.value;
  }

  return {entries, feelingsByMood, defaultFeelings, loading, saving, load, save};
}
